import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Mail, Lock, Eye, EyeOff, Users, Flag, Volume2, Shield, Activity } from "lucide-react";
import { AdminPulseSettings } from "@/components/admin-pulse-settings";
import { ClientSettings } from "./client-settings";
import { EmailSetupModal } from "./email-setup-modal";
import { EmailMessagesSection } from "./email-messages-section";
import { EmailMatchingSettings } from "./email-matching-settings";
import { EmailAdminReview } from "./email-admin-review";
import { DropboxSettings } from "./dropbox-settings";
import { DropboxReadOnlySettings } from "./dropbox-readonly-settings";
import { StatusButtonsSettings } from "./status-buttons-settings";
import { SequenceSettings } from "./sequence-settings";
import { DeveloperSettings } from "./developer-settings";
import { ErrorLogSettings } from "./error-log-settings";
import { TimeTrackingSettings } from "./time-tracking-settings";
import { UserManagementSettings } from "./user-management-settings";
import { UserAccessSettings } from "./user-access-settings";
import { ProjectLoggingSettings } from "./project-logging-settings";
import { SyncHealthMonitor } from "./sync-health-monitor";
import { EmailSyncStatus } from "./email-sync-status";
import { SyncHealthDot } from "./sync-health-dot";
import { BackupSettings } from "./backup-settings";
import { AttachmentSanityCheck } from "./attachment-sanity-check";
import { NotificationSoundsSettings } from "./notification-sounds-settings";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useDebugWindow } from "@/hooks/use-debug-render";

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Track settings modal window state
  useDebugWindow('SettingsModal', open);
  
  // All hooks must be called unconditionally before any returns
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    userManagement: false,
    userAccess: false,
    clients: false,
    emails: false,
    emailTemplates: false,
    emailMatching: false,
    emailAdminReview: false,
    dropbox: false,
    dropboxReadOnly: false,
    statusButtons: false,
    sequences: false,
    logs: false,
    timeTracking: false,
    projectLogging: false,
    debugLogging: false,
    backupRestore: false,
    attachmentSanity: false,
    notificationSounds: false,
    adminPulses: false,
    syncStatus: false,
  });
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  
  // Standard email templates — stored server-side (system settings) since
  // for reusable email template settings. localStorage is
  // only read once as a migration source for previously customized values.
  const [standardPreTemplate, setStandardPreTemplate] = useState('');
  const [standardPostTemplate, setStandardPostTemplate] = useState('');
  const templatesLoadedRef = useRef(false);

  const { data: standardTemplates } = useQuery<{ preTemplate: string; postTemplate: string }>({
    queryKey: ['/api/settings/email-standard-templates'],
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      templatesLoadedRef.current = false;
      return;
    }
    if (templatesLoadedRef.current || !standardTemplates) return;
    templatesLoadedRef.current = true;
    // One-time migration: prefer a locally customized value over the seeded
    // server default, so nothing an admin typed pre-migration is lost.
    const localPre = localStorage.getItem('standardPreEmailTemplate');
    const localPost = localStorage.getItem('standardPostEmailTemplate');
    const seededPre = 'Dear {firstName},\n\nThank you for your inquiry.';
    const seededPost = 'Best regards,\nBen';
    setStandardPreTemplate(
      (standardTemplates.preTemplate && standardTemplates.preTemplate !== seededPre)
        ? standardTemplates.preTemplate
        : (localPre || standardTemplates.preTemplate || seededPre)
    );
    setStandardPostTemplate(
      (standardTemplates.postTemplate && standardTemplates.postTemplate !== seededPost)
        ? standardTemplates.postTemplate
        : (localPost || standardTemplates.postTemplate || seededPost)
    );
  }, [open, standardTemplates]);

  const saveTemplatesMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('PATCH', '/api/settings/email-matching', {
        email_standard_pre_template: standardPreTemplate,
        email_standard_post_template: standardPostTemplate,
      });
    },
    onSuccess: () => {
      // Server is now the source of truth — retire the localStorage copies.
      localStorage.removeItem('standardPreEmailTemplate');
      localStorage.removeItem('standardPostEmailTemplate');
      queryClient.invalidateQueries({ queryKey: ['/api/settings/email-standard-templates'] });
      toast({
        title: "Templates saved",
        description: "Standard email templates have been saved for the whole team",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Save failed",
        description: error.message || "Could not save templates",
        variant: "destructive",
      });
    },
  });

  const isAdmin = user?.role === 'admin';

  // Main settings are strictly admin-only — non-admin users must never see
  // this dialog (server-side the backup/settings APIs already require admin).
  if (!user || !isAdmin) {
    return null;
  }
  
  const saveStandardTemplates = () => saveTemplatesMutation.mutate();



  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[90vw] h-[85vh] navy-card border-gray-600 text-white overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white">
            Settings
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Configure system settings, email accounts, and integrations
          </DialogDescription>
        </DialogHeader>
        
        {/* Settings content */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-4">
          {/* Admin-only email & sync status (formerly in the project/year header) */}
          {isAdmin && (
            <div className="rounded-lg border border-gray-600 navy-bg px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-300">Email &amp; Sync Status</span>
              </div>
              <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
                <div
                  className="flex items-center gap-2"
                  title="Background sync queue health (admin). Green = healthy, amber = queue growing, red = not responding."
                >
                  <SyncHealthDot />
                  <span className="text-xs text-gray-400">Background sync</span>
                </div>
                <EmailSyncStatus onNavigateToPulse={() => onOpenChange(false)} />
              </div>
            </div>
          )}

          {/* Admin-only sections */}
          {isAdmin && <>
          {/* User Management Section */}
          <Collapsible open={expandedSections.userManagement} onOpenChange={() => toggleSection('userManagement')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-400" />
                  <span className="text-lg font-medium text-white">User Management</span>
                </div>
                {expandedSections.userManagement ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <UserManagementSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* User Access Control Section */}
          <Collapsible open={expandedSections.userAccess} onOpenChange={() => toggleSection('userAccess')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-gray-400" />
                  <span className="text-lg font-medium text-white">User Access Control</span>
                </div>
                {expandedSections.userAccess ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <UserAccessSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Clients Section */}
          <Collapsible open={expandedSections.clients} onOpenChange={() => toggleSection('clients')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Clients</span>
                {expandedSections.clients ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <ClientSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Email Messages Section */}
          <Collapsible open={expandedSections.emails} onOpenChange={() => toggleSection('emails')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Email</span>
                {expandedSections.emails ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-white">Email Accounts & Messages</span>
                  <Button
                    onClick={() => setShowEmailSetup(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    E-Mail Setup
                  </Button>
                </div>
                <EmailMessagesSection />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Email Templates Section */}
          <Collapsible open={expandedSections.emailTemplates} onOpenChange={() => toggleSection('emailTemplates')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Email Templates</span>
                {expandedSections.emailTemplates ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <div className="space-y-4">
                <div className="text-sm text-gray-300 mb-4">
                  <p>Define standard email templates that can be used as suggestions for new contact persons.</p>
                  <p className="mt-2">Use <code className="px-1 py-0.5 bg-gray-700 rounded text-xs">{'{firstName}'}</code> as a placeholder for the contact's first name in the Pre-Email template.</p>
                </div>
                
                <div>
                  <Label htmlFor="standard-pre-email">Standard Pre-Email Template</Label>
                  <textarea
                    id="standard-pre-email"
                    value={standardPreTemplate}
                    onChange={(e) => setStandardPreTemplate(e.target.value)}
                    className="w-full mt-2 p-2 bg-gray-700 border border-gray-600 rounded text-white min-h-[100px]"
                    placeholder="Dear {firstName},&#10;&#10;Thank you for your inquiry..."
                  />
                </div>
                
                <div>
                  <Label htmlFor="standard-post-email">Standard Post-Email Template</Label>
                  <textarea
                    id="standard-post-email"
                    value={standardPostTemplate}
                    onChange={(e) => setStandardPostTemplate(e.target.value)}
                    className="w-full mt-2 p-2 bg-gray-700 border border-gray-600 rounded text-white min-h-[100px]"
                    placeholder="Best regards,&#10;Your Team"
                  />
                </div>
                
                <Button 
                  onClick={saveStandardTemplates}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Templates
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Email Matching Section */}
          <Collapsible open={expandedSections.emailMatching} onOpenChange={() => toggleSection('emailMatching')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <span className="text-lg font-medium text-white">Email Matching</span>
                </div>
                {expandedSections.emailMatching ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <EmailMatchingSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Email Admin Review Section */}
          <Collapsible open={expandedSections.emailAdminReview} onOpenChange={() => toggleSection('emailAdminReview')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Flag className="h-5 w-5 text-gray-400" />
                  <span className="text-lg font-medium text-white">Email Admin Review</span>
                </div>
                {expandedSections.emailAdminReview ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <EmailAdminReview />
            </CollapsibleContent>
          </Collapsible>

          {/* Admin Pulses Section */}
          <Collapsible open={expandedSections.adminPulses} onOpenChange={() => toggleSection('adminPulses')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-amber-700 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-amber-400" />
                  <span className="text-lg font-medium text-white">Admin Pulses</span>
                </div>
                {expandedSections.adminPulses ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <AdminPulseSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Dropbox Section */}
          <Collapsible open={expandedSections.dropbox} onOpenChange={() => toggleSection('dropbox')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Dropbox Storage</span>
                {expandedSections.dropbox ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <DropboxSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Read-Only Dropbox Section */}
          <Collapsible open={expandedSections.dropboxReadOnly} onOpenChange={() => toggleSection('dropboxReadOnly')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Read-Only Dropbox Browser</span>
                {expandedSections.dropboxReadOnly ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <DropboxReadOnlySettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Status Buttons Section */}
          <Collapsible open={expandedSections.statusButtons} onOpenChange={() => toggleSection('statusButtons')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Status Buttons</span>
                {expandedSections.statusButtons ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <StatusButtonsSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Sequences Section */}
          <Collapsible open={expandedSections.sequences} onOpenChange={() => toggleSection('sequences')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Sequences</span>
                {expandedSections.sequences ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <SequenceSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Time Tracking Section */}
          <Collapsible open={expandedSections.timeTracking} onOpenChange={() => toggleSection('timeTracking')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Time Tracking</span>
                {expandedSections.timeTracking ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <TimeTrackingSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Notification Sounds Section */}
          <Collapsible open={expandedSections.notificationSounds} onOpenChange={() => toggleSection('notificationSounds')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
                data-testid="button-notification-sounds-section"
              >
                <div className="flex items-center gap-2">
                  <Volume2 className="h-5 w-5 text-gray-400" />
                  <span className="text-lg font-medium text-white">Notification Sounds</span>
                </div>
                {expandedSections.notificationSounds ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <NotificationSoundsSettings />
            </CollapsibleContent>
          </Collapsible>

          {/* Project Logging Section */}
          <Collapsible open={expandedSections.projectLogging} onOpenChange={() => toggleSection('projectLogging')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
                data-testid="button-project-logging-section"
              >
                <span className="text-lg font-medium text-white">Project Audit Logs</span>
                {expandedSections.projectLogging ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <ProjectLoggingSettings />
            </CollapsibleContent>
          </Collapsible>

          </>}

          {/* Backup & Restore Section - visible to admins AND project-assigned users */}
          {isAdmin && (
          <Collapsible open={expandedSections.backupRestore} onOpenChange={() => toggleSection('backupRestore')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
                data-testid="button-backup-restore-section"
              >
                <span className="text-lg font-medium text-white">Backup & Restore</span>
                {expandedSections.backupRestore ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <BackupSettings />
            </CollapsibleContent>
          </Collapsible>
          )}

          {/* Attachment Sanity Check (Task #172) — admin-only, read-only scan */}
          {isAdmin && (
          <Collapsible open={expandedSections.attachmentSanity} onOpenChange={() => toggleSection('attachmentSanity')}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
                data-testid="button-attachment-sanity-section"
              >
                <span className="text-lg font-medium text-white">Attachment Sanity Check</span>
                {expandedSections.attachmentSanity ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <AttachmentSanityCheck />
            </CollapsibleContent>
          </Collapsible>
          )}

          {/* Sync Status Section - admin only */}
          {isAdmin && (
          <Collapsible open={expandedSections.syncStatus} onOpenChange={() => toggleSection('syncStatus')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Sync Status</span>
                {expandedSections.syncStatus ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg">
              <SyncHealthMonitor />
            </CollapsibleContent>
          </Collapsible>
          )}

          {/* Developer Settings Section (includes performance and debug logging) - admin only */}
          {isAdmin && (
          <Collapsible open={expandedSections.logs} onOpenChange={() => toggleSection('logs')}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto text-left hover:bg-gray-700 border border-gray-600 rounded-lg"
              >
                <span className="text-lg font-medium text-white">Developer Settings</span>
                {expandedSections.logs ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 p-4 border border-gray-600 rounded-lg navy-bg space-y-6">
              <ErrorLogSettings />
              <DeveloperSettings />
            </CollapsibleContent>
          </Collapsible>
          )}
        </div>
      </DialogContent>
      
      {/* Email Setup Modal */}
      <EmailSetupModal
        open={showEmailSetup}
        onOpenChange={setShowEmailSetup}
      />
    </Dialog>
  );
}